<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="cal-header">
        <oxd-text tag="h6" class="orangehrm-main-title">{{
          monthLabel
        }}</oxd-text>
        <div class="cal-nav">
          <oxd-button
            display-type="ghost"
            label="‹"
            aria-label="Previous month"
            @click="shift(-1)"
          />
          <oxd-button display-type="ghost" label="Today" @click="goToday" />
          <oxd-button
            display-type="ghost"
            label="›"
            aria-label="Next month"
            @click="shift(1)"
          />
        </div>
      </div>
      <oxd-divider />
      <div :class="['cal-grid', {'cal-grid--loading': loading}]">
        <div v-for="d in weekdays" :key="d" class="cal-weekday">{{ d }}</div>
        <div
          v-for="day in days"
          :key="day.iso"
          :class="[
            'cal-day',
            {'cal-day--other': !day.inMonth, 'cal-day--today': day.isToday},
          ]"
          role="button"
          tabindex="0"
          :aria-label="`Open ${day.iso}`"
          @click="openDay(day)"
          @keydown.enter="openDay(day)"
        >
          <div class="cal-date">{{ day.date }}</div>
          <div
            v-for="event in eventsByDay[day.iso] || []"
            :key="event.id"
            :class="['cal-event', `cal-event--${state(event)}`]"
            :title="tooltip(event)"
          >
            <a class="cal-event-main" :href="candidateUrl(event)" @click.stop>
              <span v-if="STATES[state(event)].mark" class="cal-mark">{{
                STATES[state(event)].mark
              }}</span>
              <span v-if="event.time" class="cal-time">{{ event.time }}</span>
              {{ event.candidate ? event.candidate.name : event.name }}
            </a>
            <a
              v-if="event.joinUrl"
              class="cal-join"
              :href="event.joinUrl"
              target="_blank"
              rel="noopener"
              @click.stop
              >{{ joinLabel(event) }}</a
            >
            <button
              v-if="event.joinUrl"
              type="button"
              class="cal-copy"
              :title="copyTitle(event)"
              :aria-label="copyTitle(event)"
              @click.stop="copyJoin(event)"
            >
              Copy
            </button>
          </div>
        </div>
      </div>
      <oxd-text v-if="!loading && events.length === 0" class="cal-empty">
        No interviews scheduled this month. Interviews are scheduled from a
        candidate's page.
      </oxd-text>
    </div>

    <oxd-dialog
      v-if="openedDay"
      class="orangehrm-dialog-modal cal-day-dialog"
      @update:show="openedDay = null"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">{{ openedDayLabel }}</oxd-text>
      </div>
      <oxd-divider />
      <oxd-text
        v-if="!(eventsByDay[openedDay.iso] || []).length"
        class="cal-empty"
      >
        Nothing scheduled.
      </oxd-text>
      <div
        v-for="event in eventsByDay[openedDay.iso] || []"
        :key="event.id"
        class="cal-day-event"
      >
        <div class="cal-day-time">{{ event.time || 'All day' }}</div>
        <div class="cal-day-body">
          <div class="cal-day-title">
            {{ event.name }}
            <template v-if="event.candidate">
              —
              <a :href="candidateUrl(event)">{{ event.candidate.name }}</a>
            </template>
          </div>
          <!-- everything about the meeting, as the calendar feed has it -->
          <div class="cal-day-info">{{ event.info }}</div>
          <div
            v-if="state(event) !== 'upcoming'"
            :class="['cal-state', `cal-state--${state(event)}`]"
          >
            {{ STATES[state(event)].label }}
            <span v-if="event.outcomeManual" class="cal-state-by"
              >(marked by hand)</span
            >
          </div>
          <div v-if="state(event) !== 'upcoming'" class="cal-day-actions">
            <button
              v-if="event.outcome !== 'done'"
              type="button"
              class="cal-mark-btn"
              :disabled="marking === event.id"
              @click="mark(event, 'done')"
            >
              Mark as done
            </button>
            <button
              v-if="event.outcome !== 'missed'"
              type="button"
              class="cal-mark-btn"
              :disabled="marking === event.id"
              @click="mark(event, 'missed')"
            >
              Mark as no-show
            </button>
            <button
              v-if="event.outcomeManual"
              type="button"
              class="cal-mark-btn"
              :disabled="marking === event.id"
              @click="mark(event, '')"
            >
              Clear my mark
            </button>
          </div>
          <div class="cal-day-actions">
            <template v-if="event.joinUrl">
              <a
                class="cal-join"
                :href="event.joinUrl"
                target="_blank"
                rel="noopener"
                >Join the {{ event.platform || 'meeting' }}</a
              >
              <button type="button" class="cal-copy" @click="copyJoin(event)">
                {{
                  event.candidateLink ? "Copy candidate's link" : 'Copy link'
                }}
              </button>
            </template>
            <button
              type="button"
              class="cal-delete"
              :disabled="deleting === event.id"
              @click="deleteMeeting(event)"
            >
              {{ deleting === event.id ? 'Deleting…' : 'Delete meeting' }}
            </button>
          </div>
        </div>
      </div>
    </oxd-dialog>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>

    <div class="orangehrm-card-container cal-feed">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Add to Google Calendar or Outlook
      </oxd-text>
      <oxd-divider />
      <oxd-text tag="p" class="cal-help">
        Subscribe to this private link and your calendar shows the interviews
        you see here, updated by your calendar app every few hours. Anyone with
        the link can read it: don't share it. "New link" stops the old one.
      </oxd-text>
      <div class="cal-feed-row">
        <input
          class="cal-feed-url"
          :value="feedUrl"
          readonly
          aria-label="Calendar feed link"
          @focus="$event.target.select()"
        />
        <oxd-button
          display-type="secondary"
          label="Copy"
          :disabled="!feedUrl"
          @click="copyFeed"
        />
        <oxd-button
          display-type="ghost"
          label="New link"
          :disabled="!feedUrl"
          @click="resetFeed"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {OxdDialog} from '@ohrm/oxd';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// the platforms' names on a chip (InterviewCalendarService::PLATFORMS gives the long ones)
const SHORT_PLATFORM = {
  'Jitsi Meet': 'Jitsi',
  // older meetings
  'GoTo Meeting': 'GoTo',
  'Google Meet': 'Meet',
};

// a meeting's state on the Calendar: its mark on the chip, its line in the day window
const STATES = {
  upcoming: {mark: '', label: ''},
  done: {mark: '✓', label: '✓ Done: the candidate was there'},
  missed: {mark: '✗', label: '✗ Missed: the candidate did not come'},
  past: {mark: '', label: 'Over: not marked yet'},
};

export default {
  name: 'InterviewCalendar',

  components: {
    'oxd-dialog': OxdDialog,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/recruitment/calendar',
    );
    const feedHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/recruitment/calendar/feed',
    );
    return {http, feedHttp};
  },

  data() {
    const now = new Date();
    return {
      // the meeting being deleted
      deleting: null,
      marking: null,
      STATES,
      year: now.getFullYear(),
      month: now.getMonth(),
      events: [],
      loading: false,
      feedUrl: '',
      openedDay: null,
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    };
  },

  computed: {
    openedDayLabel() {
      const [y, m, d] = this.openedDay.iso.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
    monthLabel() {
      return new Date(this.year, this.month, 1).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      });
    },
    // whole weeks, Monday first, covering the month
    days() {
      const first = new Date(this.year, this.month, 1);
      const start = new Date(first);
      start.setDate(1 - ((first.getDay() + 6) % 7));
      const last = new Date(this.year, this.month + 1, 0);
      const end = new Date(last);
      end.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7)));
      const today = iso(new Date());
      const days = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push({
          iso: iso(d),
          date: d.getDate(),
          inMonth: d.getMonth() === this.month,
          isToday: iso(d) === today,
        });
      }
      return days;
    },
    eventsByDay() {
      return this.events.reduce((byDay, event) => {
        (byDay[event.date] = byDay[event.date] || []).push(event);
        return byDay;
      }, {});
    },
  },

  watch: {
    month() {
      this.load();
    },
  },

  created() {
    this.load();
    this.feedHttp.request({method: 'GET'}).then(({data}) => {
      this.feedUrl = data.data.url;
    });
  },

  methods: {
    // Anna's place in the meeting is cancelled too, and an invited candidate is emailed
    // upcoming; then done / missed (Anna's call or HR's mark), or past with nothing known
    state(event) {
      if (event.outcome) return event.outcome;
      const start = new Date(`${event.date}T${event.time || '23:59'}`);
      return start < new Date() ? 'past' : 'upcoming';
    },
    mark(event, outcome) {
      this.marking = event.id;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/meetings/${event.id}`,
          data: {outcome},
        })
        .then(() => this.load())
        .finally(() => {
          this.marking = null;
        });
    },
    deleteMeeting(event) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation !== 'ok') return;
        this.deleting = event.id;
        this.http
          .request({method: 'DELETE', url: `/api/v2/anna/meetings/${event.id}`})
          .then(({data: {data}}) => {
            const notes = [...(data.notes || [])];
            if (data.email && !data.email.sent) notes.push(data.email.note);
            if (notes.length) {
              this.$toast.warn({
                title: 'Meeting deleted',
                message: notes.join(' '),
              });
            } else {
              this.$toast.success({
                title: 'Meeting deleted',
                message: data.email?.sent
                  ? `Meeting cancelled; the cancellation was emailed to ${data.email.to}.`
                  : 'Deleted from Orbit (it had no meeting link).',
              });
            }
            this.load();
          })
          .finally(() => {
            this.deleting = null;
          });
      });
    },
    load() {
      this.loading = true;
      this.http
        .getAll({
          fromDate: this.days[0].iso,
          toDate: this.days[this.days.length - 1].iso,
        })
        .then(({data}) => {
          this.events = data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    shift(months) {
      const d = new Date(this.year, this.month + months, 1);
      this.year = d.getFullYear();
      this.month = d.getMonth();
    },
    goToday() {
      const now = new Date();
      this.year = now.getFullYear();
      this.month = now.getMonth();
    },
    candidateUrl(event) {
      return event.candidate
        ? `${window.appGlobal.baseUrl}/recruitment/addCandidate/${event.candidate.id}`
        : null;
    },
    // the chip's link names the platform, short
    joinLabel(event) {
      return SHORT_PLATFORM[event.platform] || 'Join';
    },
    tooltip(event) {
      return [
        event.name,
        event.vacancy,
        event.interviewers.length ? event.interviewers.join(', ') : null,
        STATES[this.state(event)].label || null,
        event.platform ? `Platform: ${event.platform}` : null,
        event.joinUrl ? `Join: ${event.joinUrl}` : null,
      ]
        .filter(Boolean)
        .join('\n');
    },
    openDay(day) {
      this.openedDay = day;
    },
    // a company Jitsi meeting: the candidate's invitation link (Join is the viewer's own)
    copyTitle(event) {
      return event.candidateLink
        ? "Copy the candidate's link"
        : 'Copy the meeting link';
    },
    copyJoin(event) {
      navigator.clipboard
        ?.writeText(event.candidateLink || event.joinUrl)
        .then(() =>
          this.$toast.success({
            title: 'Copied',
            message: event.candidateLink
              ? "The candidate's link is in your clipboard: it lets them in from 15 minutes before the start."
              : 'The meeting link is in your clipboard.',
          }),
        );
    },
    copyFeed() {
      navigator.clipboard?.writeText(this.feedUrl).then(() =>
        this.$toast.success({
          title: 'Copied',
          message: 'Paste it into your calendar app as a subscription.',
        }),
      );
    },
    resetFeed() {
      if (
        !window.confirm(
          'Make a new link? Calendars subscribed to the current one stop updating.',
        )
      )
        return;
      this.feedHttp.request({method: 'PUT'}).then(({data}) => {
        this.feedUrl = data.data.url;
        this.$toast.updateSuccess();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cal-nav {
  display: flex;
  gap: 4px;
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-left: 1px solid #e8eaef;
  border-top: 1px solid #e8eaef;
  margin-top: 12px;
  &--loading {
    opacity: 0.5;
  }
}
.cal-weekday {
  font-size: 12px;
  font-weight: 600;
  color: #6a7482;
  padding: 6px 8px;
  border-right: 1px solid #e8eaef;
  border-bottom: 1px solid #e8eaef;
}
.cal-day {
  min-height: 96px;
  padding: 4px 6px 6px;
  border-right: 1px solid #e8eaef;
  border-bottom: 1px solid #e8eaef;
  overflow: hidden;
  &--other {
    background: #f8f9fa;
    .cal-date {
      color: #b0b6bf;
    }
  }
  &--today .cal-date {
    background: var(--oxd-primary-one-color);
    color: #fff;
    border-radius: 10px;
    padding: 0 6px;
  }
}
.cal-date {
  display: inline-block;
  font-size: 12px;
  color: #64728c;
  margin-bottom: 4px;
}
.cal-event {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.3;
  padding: 2px 6px;
  margin-bottom: 3px;
  border-radius: 4px;
  background: var(--oxd-primary-one-alpha-10-color);
  &:hover {
    background: var(--oxd-primary-one-alpha-20-color);
  }
}
.cal-event--done {
  background: #e3f3e6;
  .cal-mark {
    color: #2e7d32;
  }
}
.cal-event--missed {
  background: #fdecea;
  .cal-event-main {
    text-decoration: line-through;
    color: #8a5a57;
  }
  .cal-mark {
    color: #c62828;
  }
}
.cal-event--past {
  background: #eef1f5;
  .cal-event-main {
    color: #64728c;
  }
}
.cal-mark {
  font-weight: 700;
  margin-right: 2px;
}
.cal-state {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
  &--done {
    color: #2e7d32;
  }
  &--missed {
    color: #c62828;
  }
  &--past {
    color: #64728c;
  }
}
.cal-state-by {
  font-weight: 400;
  color: #64728c;
}
.cal-mark-btn {
  border: 1px solid #d5dbe5;
  background: #fff;
  border-radius: 999px;
  padding: 3px 12px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
.cal-event-main {
  flex: 1;
  min-width: 0;
  color: #1f2a3c;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cal-join {
  flex: none;
  font-weight: 600;
  color: var(--oxd-primary-one-color);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.cal-day {
  cursor: pointer;
  &:hover {
    background: #f4f7fb;
  }
}
.cal-copy {
  flex: none;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #64728c;
  cursor: pointer;
  &:hover {
    color: var(--oxd-primary-one-color);
    text-decoration: underline;
  }
}
.cal-day-info {
  white-space: pre-line;
  font-size: 13px;
  line-height: 1.55;
  color: #3d4757;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 6px 0 8px;
  user-select: text;
}
.cal-delete {
  border: none;
  background: none;
  color: #c62828;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  margin-left: auto;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
.cal-day-dialog {
  min-width: min(640px, 92vw);
}
.cal-day-event {
  display: flex;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #eef1f5;
  &:last-child {
    border-bottom: none;
  }
}
.cal-day-time {
  flex: none;
  width: 70px;
  font-weight: 700;
  color: #1f2a3c;
}
.cal-day-body {
  flex: 1;
  min-width: 0;
}
.cal-day-title {
  font-weight: 600;
  color: #1f2a3c;
  a {
    color: var(--oxd-primary-one-color);
  }
}
.cal-day-meta {
  font-size: 13px;
  color: #6a7482;
  margin-top: 2px;
}
.cal-day-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
}
.cal-day-link {
  color: #8a94a6;
  font-size: 12px;
  overflow-wrap: anywhere;
}
.cal-time {
  font-weight: 600;
  margin-right: 4px;
}
.cal-empty,
.cal-help {
  color: #6a7482;
  font-size: 13px;
  padding-top: 12px;
}
.cal-feed {
  margin-top: 16px;
}
.cal-feed-row {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.cal-feed-url {
  flex: 1 1 320px;
  min-width: 0;
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e8eaef;
  border-radius: 8px;
  color: #64728c;
  background: #f8f9fa;
}
</style>
